import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  OrderEditingFlatTeamSettingGroupById,
  type OrderEditingFlatTeamSettingGroupId,
} from "./groups";

type OrderEditingSettingInput = SettingRegistryMetadataInput;

function setting(metadata: OrderEditingSettingInput): OrderEditingSettingInput {
  return metadata;
}

function orderEditingGroup<
  const Group extends OrderEditingFlatTeamSettingGroupId,
  const T extends Record<string, OrderEditingSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

const restockValueSemantics = {
  defaultValue: true,
  falseLabel: "Do not restock",
  trueLabel: "Restock",
} as const;

const OrderEditingRegistry = {
  ...orderEditingGroup("order_editing.subscription_editing", {
    "orderEditing/subscriptionEditing/enabled": setting({
      label: "Subscription editing",
      description:
        "Customers verify their email before making changes. Existing orders are unchanged.",
      synonyms: [],
      valueSemantics: { defaultValue: false },
      changeGuidance: {
        warning:
          "Saving order editing settings without a connected Recharge integration turns off subscription editing and all subscription permissions.",
      },
      availability: {
        conditions: [
          {
            type: "evaluator",
            evaluator: "integration.connected",
            integration: "recharge",
          },
        ],
        unavailableBehavior: "hidden",
        reason: "Connect Recharge to enable subscription editing.",
      },
    }),
    "orderEditing/subscriptionEditing/quantity": setting({
      label: "Change subscription quantity",
      description: "Change the number of items in each delivery.",
      synonyms: [],
      valueSemantics: { defaultValue: false },
      availability: {
        conditions: [
          {
            type: "evaluator",
            evaluator: "integration.connected",
            integration: "recharge",
          },
          {
            type: "settingEquals",
            settingId: "orderEditing/subscriptionEditing/enabled",
            value: true,
          },
        ],
        unavailableBehavior: "hidden",
        reason:
          "Enable subscription editing to configure customer permissions.",
      },
    }),
    "orderEditing/subscriptionEditing/frequency": setting({
      label: "Change delivery frequency",
      description: "Choose from the available delivery schedules.",
      synonyms: [],
      valueSemantics: { defaultValue: false },
      availability: {
        conditions: [
          {
            type: "evaluator",
            evaluator: "integration.connected",
            integration: "recharge",
          },
          {
            type: "settingEquals",
            settingId: "orderEditing/subscriptionEditing/enabled",
            value: true,
          },
        ],
        unavailableBehavior: "hidden",
        reason:
          "Enable subscription editing to configure customer permissions.",
      },
    }),
    "orderEditing/subscriptionEditing/nextDate": setting({
      label: "Change next delivery date",
      description: "Move the next charge to a future date.",
      synonyms: [],
      valueSemantics: { defaultValue: false },
      availability: {
        conditions: [
          {
            type: "evaluator",
            evaluator: "integration.connected",
            integration: "recharge",
          },
          {
            type: "settingEquals",
            settingId: "orderEditing/subscriptionEditing/enabled",
            value: true,
          },
        ],
        unavailableBehavior: "hidden",
        reason:
          "Enable subscription editing to configure customer permissions.",
      },
    }),
    "orderEditing/subscriptionEditing/skip": setting({
      label: "Skip next delivery",
      description: "Skip one delivery and keep the subscription active.",
      synonyms: [],
      valueSemantics: { defaultValue: false },
      availability: {
        conditions: [
          {
            type: "evaluator",
            evaluator: "integration.connected",
            integration: "recharge",
          },
          {
            type: "settingEquals",
            settingId: "orderEditing/subscriptionEditing/enabled",
            value: true,
          },
        ],
        unavailableBehavior: "hidden",
        reason:
          "Enable subscription editing to configure customer permissions.",
      },
    }),
    "orderEditing/subscriptionEditing/cancel": setting({
      label: "Cancel subscription",
      description: "Stop future deliveries. Existing orders are unchanged.",
      synonyms: [],
      valueSemantics: { defaultValue: false },
      availability: {
        conditions: [
          {
            type: "evaluator",
            evaluator: "integration.connected",
            integration: "recharge",
          },
          {
            type: "settingEquals",
            settingId: "orderEditing/subscriptionEditing/enabled",
            value: true,
          },
        ],
        unavailableBehavior: "hidden",
        reason:
          "Enable subscription editing to configure customer permissions.",
      },
    }),
    "orderEditing/subscriptionEditing/address": setting({
      label: "Change subscription shipping address",
      description:
        "Update future deliveries. Shared addresses change for all subscriptions using them.",
      synonyms: [],
      valueSemantics: { defaultValue: false },
      availability: {
        conditions: [
          {
            type: "evaluator",
            evaluator: "integration.connected",
            integration: "recharge",
          },
          {
            type: "settingEquals",
            settingId: "orderEditing/subscriptionEditing/enabled",
            value: true,
          },
        ],
        unavailableBehavior: "hidden",
        reason:
          "Enable subscription editing to configure customer permissions.",
      },
    }),
  }),

  // TODO(Connor Ellis): these entries write the legacy global
  // `orderEditing.tools`, not the per-rule `tools` the editing rules UI shows,
  // so a catalog edit to them has no effect. Point them at rules.
  ...orderEditingGroup("order_editing.editing_rules", {
    "orderEditing/tools/cancelOrder/restockInventory": setting({
      label: "Restock inventory",
      description:
        "Return items to available inventory when the order is cancelled",
      synonyms: [
        "restock inventory on cancel",
        "return inventory when cancelling order",
        "put inventory back on cancel",
        "restock cancelled order items",
      ],
      valueSemantics: restockValueSemantics,
    }),
    "orderEditing/tools/updateOrder/removeItemRestockInventory": setting({
      label: "Restock inventory",
      description:
        "Return items to available inventory when they are removed from the order",
      synonyms: [
        "restock inventory when removing items",
        "return inventory when removing order items",
        "put inventory back when removing items",
        "restock removed order items",
      ],
      valueSemantics: restockValueSemantics,
    }),
    "orderEditing/tools/updateOrder/changeVariantRestockInventory": setting({
      label: "Restock inventory",
      description:
        "Return the original variant to available inventory when it is swapped",
      synonyms: [
        "restock inventory when changing variant",
        "return original variant to inventory",
        "put original variant back in stock",
        "restock swapped variant",
      ],
      valueSemantics: restockValueSemantics,
    }),
    "orderEditing/updateShippingPriceOnOrderEdit": setting({
      label: "Discount shipping on order edit",
      description:
        "When an automatic discount reduces shipping after an order edit, replace the shipping line with a discounted one. When off, shipping discounts are not applied and the order total can be higher than Shopify's calculation.",
      synonyms: [
        "shipping discount on order edit",
        "update shipping price when editing order",
        "discount shipping line when order edited",
      ],
      valueSemantics: {
        defaultValue: false,
        falseLabel: "Do not discount shipping",
        trueLabel: "Discount shipping",
      },
    }),
  }),
};

type OrderEditingFlatTeamSettingKey = keyof typeof OrderEditingRegistry;
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const OrderEditingFlatTeamSettingKeys = Object.keys(
  OrderEditingRegistry,
) as OrderEditingFlatTeamSettingKey[];

export const OrderEditingFlatTeamSettingCatalogDefinitions =
  OrderEditingFlatTeamSettingKeys.map((key) => {
    const definition = OrderEditingRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: OrderEditingFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    OrderEditingFlatTeamSettingKey,
    OrderEditingFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<OrderEditingFlatTeamSettingKey>
  >[];

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const OrderEditingFlatTeamSettingMetadataByKey = Object.fromEntries(
  OrderEditingFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<OrderEditingFlatTeamSettingKey, SettingMetadata>;
